import React, { useEffect, useMemo, useState } from 'react';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';
import { NumericProps } from '@swyftx/aviary/atoms/Typography/Numeric/Numeric.styles';
import { EnhancedTooltip } from '@swyftx/aviary/molecules/EnhancedTooltip/EnhancedTooltip';
import { MarginPrice } from '@swyftx/margin/build/types';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';
import { cn } from '@shared/utils/lib/ui';

import { useDebounce } from 'react-use';
import { useMarginInstruments } from 'src/lib/margin/hooks/instruments';
import { useMarginPrices } from 'src/lib/margin/hooks/prices';

type Props = {
  title?: string;
  size?: NumericProps['size'];
  dynamicColor?: boolean;
  dataKey: keyof MarginPrice;
  className?: string;
  tooltip?: string;
  name?: string;
};

const MarginInstrumentPanelDataItem: React.FC<Props> = ({
  title,
  size = 'small',
  dynamicColor,
  tooltip,
  name,
  dataKey,
  className,
}) => {
  const [lastPrice, setLastPrice] = useState<MarginPrice>();
  const { selectedMarketPrice } = useMarginPrices();
  const { selectedMarket } = useMarginInstruments();
  const [color, setColor] = useState<NumericProps['color']>('primary');
  const [opacity, setOpacity] = useState<number>(0);

  useDebounce(() => setLastPrice(selectedMarketPrice), 100, [selectedMarketPrice]);

  const priceDirection = useMemo(() => {
    if (!lastPrice) return 'none';
    if (Big(lastPrice.bid).eq(Big(selectedMarketPrice?.bid))) return 'same';

    return Big(selectedMarketPrice?.[dataKey]).gt(lastPrice[dataKey]) ? 'up' : 'down';
  }, [dataKey, lastPrice, selectedMarketPrice]);

  const updateOpacity = () => {
    setOpacity(1);
    window.setTimeout(() => setOpacity(0), 250);
  };

  useEffect(() => {
    if (dataKey === 'bid') {
      if (priceDirection === 'none' || priceDirection === 'same') return;

      setColor(priceDirection === 'up' ? 'success' : 'error');
      updateOpacity();
    }
    if (dynamicColor) {
      setColor(Big(selectedMarketPrice?.[dataKey]).gte(0) ? 'success' : 'error');
    }
  }, [dataKey, dynamicColor, priceDirection, selectedMarketPrice]);

  return (
    <FlexLayout direction='column' justifyContent='center' className={cn('relative h-full', className)} spacing={4}>
      {title && (
        <EnhancedTooltip name={name} title={tooltip} align='center' side='top'>
          <span>
            <Body size='xsmall' color='secondary' underline='dashed' className='whitespace-nowrap'>
              {title}
            </Body>
          </span>
        </EnhancedTooltip>
      )}
      <Numeric weight='bold' size={size} loading={!selectedMarketPrice} color={dynamicColor ? color : 'primary'}>
        {formatCurrency(selectedMarketPrice?.[dataKey], undefined, {
          priceScale: dataKey === 'bidChangeYesterday' ? 2 : selectedMarket?.precision,
        })}
        {dataKey === 'bidChangeYesterday' ? '%' : ''}
      </Numeric>
      {dataKey === 'bid' && !dynamicColor && (
        <Numeric
          weight='bold'
          size={size}
          style={{ opacity }}
          loading={!selectedMarketPrice}
          className='duration-[800ms] absolute transition-[opacity]'
          color={color}
        >
          {formatCurrency(selectedMarketPrice?.[dataKey], undefined, {
            priceScale: selectedMarket?.precision,
          })}
        </Numeric>
      )}
    </FlexLayout>
  );
};

export { MarginInstrumentPanelDataItem };
